import React, { useState, useEffect } from 'react';
import { isEmpty } from 'lodash';
import {
  Button,
  Box,
  CircularProgress,
  Divider,
  Typography
} from '@mui/material';

import ConfirmationDialog from '../../../components/ConfirmationDialog/ConfirmationDialog';
import DialogModal from '../../../components/DialogModal';
import DropdownSelect from '../../../components/DropdownSelect';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import { SnackbarSeverityType } from '../../../enums';
import { StyledButton } from '../styled';
import { SubjectResponse, ScoringType, GradeScale, Subject } from '../types';
import pallet from '../../../layouts/pallet';
import MarkBookSectionTable from './MarkBookSectionTable';
import { useMarkBookRows, MARK_BOOK_SECTION } from '../hooks/useMarkBookRows';

interface EditSubjectModalProps {
  open: boolean;
  onClose: () => void;
  subjectToEdit?: SubjectResponse;
  onUpdateSubject: (updatedSubjectData: SubjectResponse) => void;
  gradeScaleOptions: GradeScale[];
  school?: string;
  scoringTypeOptions: ScoringType[];
  isLoadingScoringTypes: boolean;
  isLoadingGradeScales: boolean;
  subjectOptions: Subject[];
  isLoadingSubjects: boolean;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
  markBookEnabled: boolean;
}

const EditSubjectModal: React.FC<EditSubjectModalProps> = ({
  open,
  onClose,
  subjectToEdit,
  onUpdateSubject,
  gradeScaleOptions,
  school,
  scoringTypeOptions,
  isLoadingScoringTypes,
  isLoadingGradeScales,
  subjectOptions,
  isLoadingSubjects,
  showToast,
  markBookEnabled
}) => {
  const [scoringType, setScoringType] = useState(
    subjectToEdit?.scoringType.id || ''
  );
  const [gradeScale, setGradeScale] = useState(
    subjectToEdit?.gradeScale?.id || ''
  );
  const [maxMarks, setMaxMarks] = useState(
    subjectToEdit?.maxMarks?.toString() || ''
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const {
    componentRows,
    skillRows,
    rowToDelete,
    addRow,
    updateRow,
    requestDeleteRow,
    confirmDeleteRow,
    cancelDeleteRow,
    resetRows,
    mapRowToComponent,
    mapRowToSkill,
    areAllRowsValid
  } = useMarkBookRows(scoringTypeOptions);

  useEffect(() => {
    if (subjectToEdit) {
      setScoringType(subjectToEdit.scoringType.id || '');
      setGradeScale(subjectToEdit.gradeScale?.id || '');
      setMaxMarks(subjectToEdit.maxMarks?.toString() || '');
      resetRows(subjectToEdit.components, subjectToEdit.skills);
    }
  }, [subjectToEdit, resetRows]);

  useEffect(() => {
    if (open) {
      if (!subjectToEdit) {
        setScoringType('');
        setGradeScale('');
        setMaxMarks('');
        resetRows();
      } else {
        setScoringType(subjectToEdit.scoringType.id || '');
        setGradeScale(subjectToEdit.gradeScale?.id || '');
        setMaxMarks(subjectToEdit.maxMarks?.toString() || '');
        resetRows(subjectToEdit.components, subjectToEdit.skills);
      }
      setIsUpdating(false);
    }
  }, [open, subjectToEdit, resetRows]);

  const handleUpdate = () => {
    setIsUpdating(true);
    if (subjectToEdit) {
      const selectedSubject = subjectOptions.find(
        (opt) => opt.id === subjectToEdit.subject.id
      );
      const selectedScoringType = scoringTypeOptions.find(
        (opt) => opt.id === scoringType
      );
      const selectedGradeScale = gradeScaleOptions.find(
        (opt) => opt.id === gradeScale
      );

      if (!selectedSubject || !selectedScoringType) {
        showToast('Please select required fields.', SnackbarSeverityType.error);
        setIsUpdating(false);
        return;
      }

      if (markBookEnabled && !areAllRowsValid) {
        showToast(
          'Please fill all required fields in components and skills.',
          SnackbarSeverityType.error
        );
        setIsUpdating(false);
        return;
      }

      const numericMax = isEmpty(maxMarks) ? undefined : parseInt(maxMarks, 10);
      const updatedSubject: SubjectResponse = {
        ...subjectToEdit,
        subject: selectedSubject as Subject,
        scoringType: selectedScoringType as ScoringType,
        ...(selectedGradeScale
          ? { gradeScale: selectedGradeScale as GradeScale }
          : { gradeScale: undefined }),
        ...(needsMaxMarks && numericMax !== undefined
          ? { maxMarks: numericMax }
          : { maxMarks: undefined }),
        ...(markBookEnabled
          ? {
              components: componentRows.map(mapRowToComponent),
              skills: skillRows.map(mapRowToSkill)
            }
          : {})
      };
      onUpdateSubject(updatedSubject);
      onClose();
    } else {
      showToast('No subject selected for update.', SnackbarSeverityType.error);
    }
    setIsUpdating(false);
  };

  const selectedScoringType = scoringTypeOptions.find(
    (st) => st.id === scoringType
  );
  const needsMaxMarks =
    !!scoringType &&
    (!selectedScoringType?.validScores ||
      selectedScoringType.validScores.length === 0);

  useEffect(() => {
    if (!needsMaxMarks) {
      setMaxMarks('');
    }
  }, [needsMaxMarks]);
  const isUpdateButtonDisabled =
    !scoringType || (needsMaxMarks && !maxMarks) || isUpdating;

  return (
    <>
      <DialogModal
        open={open}
        onClose={onClose}
        title='Edit Subject in Exam'
        maxWidth='md'
        actions={
          <>
            <Button
              onClick={onClose}
              color='primary'
              sx={{ border: `1px solid ${pallet.primary}` }}
            >
              Cancel
            </Button>
            <StyledButton
              onClick={handleUpdate}
              disabled={isUpdateButtonDisabled}
            >
              {isUpdating ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Update'
              )}
            </StyledButton>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <DropdownSelect
            label='Subject Name'
            placeholder='Select Subject'
            name='subjectName'
            value={subjectToEdit?.subject.id || ''}
            options={subjectOptions.map((sub) => ({
              value: sub.id || '',
              label: sub.name
            }))}
            onChange={(e) => {}}
            fullWidth
            loading={isLoadingSubjects}
            disabled
          />
          <DropdownSelect
            label='Scoring Type'
            placeholder='Select Scoring Type'
            name='scoringType'
            value={scoringType}
            options={scoringTypeOptions.map((st) => ({
              value: st.id || '',
              label: st.name
            }))}
            onChange={(e) => setScoringType(e.target.value as string)}
            required
            fullWidth
            loading={isLoadingScoringTypes}
          />
          {needsMaxMarks && (
            <TextInputWithIcon
              label='Max Marks'
              placeholder='Enter Max Marks'
              name='maxMarks'
              value={maxMarks}
              onChange={(e) => setMaxMarks(e.target.value)}
              fullWidth
              type='number'
              required
            />
          )}
          <DropdownSelect
            label='Grade Scale'
            placeholder='Select Grade Scale'
            name='gradeScale'
            value={gradeScale}
            options={gradeScaleOptions.map((gs) => ({
              value: gs.id || '',
              label: gs.name
            }))}
            onChange={(e) => setGradeScale(e.target.value as string)}
            fullWidth
            loading={isLoadingGradeScales}
          />
          {markBookEnabled && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant='subtitle2' fontWeight='bold'>
                Components
              </Typography>
              <MarkBookSectionTable
                firstFieldLabel='Name'
                rows={componentRows}
                scoringTypeOptions={scoringTypeOptions}
                isLoadingScoringTypes={isLoadingScoringTypes}
                onUpdateRow={(update) =>
                  updateRow(MARK_BOOK_SECTION.COMPONENT, update)
                }
                onAddRow={() => addRow(MARK_BOOK_SECTION.COMPONENT)}
                onRequestDeleteRow={(index) =>
                  requestDeleteRow(MARK_BOOK_SECTION.COMPONENT, index)
                }
              />
              <Divider sx={{ my: 1 }} />
              <Typography variant='subtitle2' fontWeight='bold'>
                Skills
              </Typography>
              <MarkBookSectionTable
                firstFieldLabel='Description'
                rows={skillRows}
                scoringTypeOptions={scoringTypeOptions}
                isLoadingScoringTypes={isLoadingScoringTypes}
                onUpdateRow={(update) =>
                  updateRow(MARK_BOOK_SECTION.SKILL, update)
                }
                onAddRow={() => addRow(MARK_BOOK_SECTION.SKILL)}
                onRequestDeleteRow={(index) =>
                  requestDeleteRow(MARK_BOOK_SECTION.SKILL, index)
                }
              />
            </>
          )}
        </Box>
      </DialogModal>
      <ConfirmationDialog
        open={rowToDelete !== null}
        onClose={cancelDeleteRow}
        message='Are you sure you want to delete this row?'
        onConfirm={confirmDeleteRow}
      />
    </>
  );
};

export default EditSubjectModal;
